import { AxiosError, AxiosResponse } from 'axios';
import { useQuery } from '../../../../../request/hooks/useQuery';
import { ObjectsConfigAPI } from '../../../../api/ObjectsConfigAPI';
import { ObjectsConfigStatus, ObjectsConfigStatusEnum } from '../../../interfaces/object';
import { useMutation } from '../../../../../request/hooks/useMutation';
import { SourceAPI } from '../../../../../sources/api/SourceAPI';
import { EntityUIState, MutationState } from '../../../../../request/interfaces/entity-ui-state';
import { getErrorMessageFromAxiosError } from '../../../../../request/utils';

export function useObjectsConfigStatus(pipelineId: number, sourceId: number) {
  const {
    data: objectsConfigStatus,
    fetch: fetchObjectsConfigStatus,
    uiState: objectsStatusFetchState,
    error: objectsStatusFetchError
  } = useQuery({
    dataSource$: async () => {
      const promises = [
        ObjectsConfigAPI.getObjectsConfigStatus(pipelineId),
        SourceAPI.getCatalogRefreshStatus(sourceId)
      ];

      return Promise.all(promises).then(([catalogStatusRes, refreshRes]) => {
        if (catalogStatusRes.data.status === ObjectsConfigStatusEnum.FAILED) {
          throw new AxiosError('Failed to create objects list', undefined, undefined, undefined, {
            status: 400,
            data: {
              message: 'Failed to create objects list'
            }
          } as AxiosResponse);
        }

        return {
          catalogStatus: catalogStatusRes.data as ObjectsConfigStatus,
          refreshStatus: refreshRes.data as { in_progress: boolean }
        };
      });
    },
    pollInterval: 5000,
    initialDelay: 2000,
    initialData: {
      catalogStatus: {
        status: ObjectsConfigStatusEnum.REQUESTED
      },
      refreshStatus: {
        in_progress: true
      }
    },
    pollUntil: d => {
      const { catalogStatus, refreshStatus } = d as {
        catalogStatus: ObjectsConfigStatus;
        refreshStatus: { in_progress: boolean };
      };

      return (
        catalogStatus.status === ObjectsConfigStatusEnum.REQUESTED || refreshStatus.in_progress
      );
    },
    terminatePollOnError: true
  });

  const { mutate: refreshSchema, mutationState } = useMutation({
    mutationTarget$: () => SourceAPI.refreshSchema(sourceId),
    onMutationSuccess: () => {
      fetchObjectsConfigStatus();
    }
  });

  const submittingRefreshSchemaRequest = mutationState === MutationState.MUTATING;

  const refreshCompleted = objectsConfigStatus
    ? !objectsConfigStatus.refreshStatus.in_progress
    : false;

  const refreshInProgress = !!objectsConfigStatus?.refreshStatus.in_progress;

  const showStatusError =
    objectsStatusFetchState === EntityUIState.ERRORED ||
    objectsConfigStatus?.catalogStatus.status === ObjectsConfigStatusEnum.FAILED;

  const showStatusLoader =
    submittingRefreshSchemaRequest ||
    refreshInProgress ||
    objectsStatusFetchState === EntityUIState.LOADING ||
    objectsConfigStatus?.catalogStatus.status === ObjectsConfigStatusEnum.REQUESTED;

  const statusErrorMessage = objectsStatusFetchError
    ? getErrorMessageFromAxiosError(objectsStatusFetchError)
    : 'Failed to create objects list';

  const showRetryCTAOnError = objectsStatusFetchState === EntityUIState.ERRORED;

  const isRefreshAndCatalogStatusComplete =
    !submittingRefreshSchemaRequest &&
    !refreshInProgress &&
    objectsConfigStatus?.catalogStatus.status === ObjectsConfigStatusEnum.COMPLETED &&
    refreshCompleted;

  const canFetchObjectsConfig =
    objectsConfigStatus?.catalogStatus.status === ObjectsConfigStatusEnum.COMPLETED &&
    refreshCompleted;

  return {
    fetchObjectsConfigStatus,
    showStatusError,
    showStatusLoader,
    statusErrorMessage,
    showRetryCTAOnError,
    isRefreshAndCatalogStatusComplete,
    canFetchObjectsConfig,
    refreshCompleted,
    submittingRefreshSchemaRequest,
    refreshSchema
  };
}
